import React, { useState, forwardRef, useImperativeHandle, useEffect, useMemo, useRef } from "react";
import { Accordion, AccordionDetails, LinearProgress } from "@mui/material";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Schedule } from "@iventis/domain-model/model/schedule";
import { ScheduleConfigEventBlockType } from "@iventis/domain-model/model/scheduleConfigEventBlockType";
import { DataField } from "@iventis/domain-model/model/dataField";
import { Content } from "@iventis/translations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    borderRadius,
    muiInputFormsCSS,
    Body2,
    formPadding,
    inputMarginBottom,
    Header5,
    labelMargin,
    inlineTextIconMargin,
    useAutoTransitionHeight,
    media,
    sectionalMargin,
    styled,
} from "@iventis/styles";
import { Theme } from "@emotion/react";
import { ScheduleConfigVenueLocationType } from "@iventis/domain-model/model/scheduleConfigVenueLocationType";
import { ScheduleConfigVersionType } from "@iventis/domain-model/model/scheduleConfigVersionType";

import { useMutation, useQuery } from "@tanstack/react-query";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DcasConfig } from "@iventis/domain-model/model/dcasConfig";
import { EditVenueTypesComponent } from "./edit-venue-types";
import { ScheduleConfigService } from "./api";
import {
    ConfigSectionsKey,
    createEmptySchedule,
    eventBlockTypeValidation,
    runSheetValidation,
    scheduleVersionTypeValidation,
    timesAndDatesValidation,
    venueTypeHierarchyValidation,
} from "./schedule-config-helpers";
import { ScheduleTimesComponent } from "./schedule-times";
import { EditEventBlockTypesComponent } from "./edit-event-block-types";
import { EditScheduleVersionTypes } from "./edit-schedule-version-types";
import { EditDcas } from "./edit-dcas";
import { ConfigSectionHeader } from "./schedule-config-section-header";

interface ScheduleConfigComponentProps<ErrorType> {
    scheduleConfigService: ScheduleConfigService;
    onSaving: (saving: boolean) => void;
    onSuccess: (schedule: Schedule) => void;
    setValidation: (valid: boolean) => void;
    extractNetworkError: (error: ErrorType) => string;
}

export interface ScheduleConfigComponentRef {
    save: () => void;
}

const ScheduleQueryClientKey = "schedule";

/**
 * Since you can't have a generic parameter with a forwardRef, we have to use a component factory
 */
export const CreateScheduleConfigComponent = <ErrorType extends Error>() =>
    forwardRef<ScheduleConfigComponentRef, ScheduleConfigComponentProps<ErrorType>>(({ scheduleConfigService, onSaving, onSuccess, extractNetworkError, setValidation }, ref) => {
        const translate = useIventisTranslate();
        const [expandedPanel, setExpandedPanel] = useState<ConfigSectionsKey>();
        const [schedule, setSchedule] = useState<Schedule>();
        const [validationErrors, setValidationErrors] = useState<string[]>();
        const transition = useAutoTransitionHeight({ initiallyExpanded: false });
        const isScheduleConfigPreviouslySaved = useRef(true);

        useEffect(() => {
            if (validationErrors) {
                transition.setExpanded(true);
            } else {
                transition.setExpanded(false);
            }
        }, [validationErrors]);

        const apiSchedule = useQuery([ScheduleQueryClientKey], { queryFn: () => scheduleConfigService.getConfiguration() });

        const { mutate } = useMutation<Schedule, unknown, Schedule>((newSchedule) => scheduleConfigService.updateConfiguration(newSchedule), {
            onSuccess: (newSchedule) => {
                onSuccess(newSchedule);
                setValidationErrors(undefined);
            },
            onError: (error) => {
                const errorMessage = extractNetworkError(error as ErrorType);
                if (typeof errorMessage === "string") {
                    setValidationErrors(errorMessage.split(`\n`));
                }
            },
            onSettled: () => {
                onSaving(false);
            },
        });

        useEffect(() => {
            if (!schedule && apiSchedule?.data) {
                setSchedule(apiSchedule.data);
            }
            if (!schedule && apiSchedule.status === "success" && typeof apiSchedule?.data?.config !== "object") {
                setSchedule(createEmptySchedule());
                isScheduleConfigPreviouslySaved.current = false;
            }
        }, [apiSchedule]);

        useImperativeHandle(ref, () => ({
            save: () => {
                onSaving(true);
                mutate(schedule);
            },
        }));

        const configSections = {
            timesAndDates: {
                key: ConfigSectionsKey.timesAndDates,
                title: translate(Content.settings.scheduleConfig.timesAndDates),
            },
            venueTypeHierarchy: {
                key: ConfigSectionsKey.venueTypeHierarchy,
                title: translate(Content.settings.scheduleConfig.venueTypeHierarchy),
            },
            scheduleVersionHierarchy: {
                key: ConfigSectionsKey.scheduleVersionHierarchy,
                title: translate(Content.settings.scheduleConfig.scheduleVersionHierarchy),
            },
            eventBlockHierarchy: {
                key: ConfigSectionsKey.eventBlockHierarchy,
                title: translate(Content.settings.scheduleConfig.eventBlockHierarchy),
            },
            venueRunSheets: {
                key: ConfigSectionsKey.venueRunSheets,
                title: translate(Content.settings.scheduleConfig.venueRunSheets),
            },
        };

        // Validation

        const isTimesAndDatesValid = useMemo(() => timesAndDatesValidation(schedule), [
            schedule?.dayStartAt,
            schedule?.dayEndAt,
            schedule?.dayZero,
            schedule?.startAt,
            schedule?.endAt,
        ]);

        const isVenueTypesValid = useMemo(() => venueTypeHierarchyValidation(schedule?.config.venueLocationTypes), [schedule?.config.venueLocationTypes]);

        const isScheduleVersionValid = useMemo(() => scheduleVersionTypeValidation(schedule?.config?.scheduleVersionTypes), [schedule?.config?.scheduleVersionTypes]);

        const isEventBlockTypesValid = useMemo(() => eventBlockTypeValidation(schedule?.config?.eventBlockTypes), [schedule?.config?.eventBlockTypes]);

        const isRunSheetValid = useMemo(() => runSheetValidation(schedule?.config?.eventBlockTypes), [schedule?.config?.eventBlockTypes]);

        useEffect(() => {
            setValidation(isTimesAndDatesValid && isVenueTypesValid && isScheduleVersionValid && isEventBlockTypesValid && isRunSheetValid);
        }, [isTimesAndDatesValid, isVenueTypesValid, isScheduleVersionValid, isEventBlockTypesValid, isRunSheetValid]);

        // Handlers

        const venueTypesChanged = (types: ScheduleConfigVenueLocationType[]) => {
            setSchedule((previous) => ({ ...previous, config: { ...previous.config, venueLocationTypes: types } }));
        };

        const dataFieldsChanged = (dataFields: DataField[]) => {
            setSchedule((previous) => ({ ...previous, config: { ...previous.config, dataFields: [...dataFields] } }));
        };

        const eventBlockTypeChanged = (eventBlockTypes: ScheduleConfigEventBlockType[]) => {
            setSchedule((previous) => ({ ...previous, config: { ...previous.config, eventBlockTypes } }));
        };

        const scheduleVersionTypeChanged = (scheduleVerisonTypes: ScheduleConfigVersionType[]) => {
            setSchedule((previous) => ({ ...previous, config: { ...previous.config, scheduleVersionTypes: [...scheduleVerisonTypes] } }));
        };

        const dcasChange = (dcasConfig: DcasConfig) => {
            setSchedule((previous) => ({ ...previous, config: { ...previous.config, dcasConfig: { ...dcasConfig } } }));
        };

        return (
            <StyledProjectScheduleConfigContainer>
                <LinearProgress style={{ marginBottom: sectionalMargin, visibility: apiSchedule.isLoading ? "visible" : "hidden" }} />
                <StyledValidationErrors ref={(element) => transition.setElementRef(element)} show={validationErrors?.length > 0}>
                    {/* Use margin for padding, since you cannot have a zero-height element with padding. The transition hook will not work without this. */}
                    <div className="padding">
                        <Header5>{translate(Content.settings.scheduleConfig.problems, { problemCount: validationErrors?.length })}</Header5>
                        <ul>
                            {validationErrors?.map((errorText) => (
                                <li key={errorText}>
                                    <FontAwesomeIcon icon={["fas", "circle"]} size="xs" />
                                    <Body2>{errorText}</Body2>
                                </li>
                            ))}
                        </ul>
                    </div>
                </StyledValidationErrors>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Accordion
                        disabled={apiSchedule.isLoading}
                        expanded={expandedPanel === configSections.timesAndDates.key}
                        onChange={(_, expanded) => (expanded ? setExpandedPanel(configSections.timesAndDates.key) : setExpandedPanel(undefined))}
                    >
                        <ConfigSectionHeader title={configSections.timesAndDates.title} valid={isTimesAndDatesValid} />
                        <AccordionDetails>
                            <ScheduleTimesComponent onChange={(schedule) => setSchedule(schedule)} schedule={schedule} />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion
                        disabled={apiSchedule.isLoading}
                        TransitionProps={{ unmountOnExit: true }}
                        expanded={expandedPanel === configSections.venueTypeHierarchy.key}
                        onChange={(_, expanded) => (expanded ? setExpandedPanel(configSections.venueTypeHierarchy.key) : setExpandedPanel(undefined))}
                    >
                        <ConfigSectionHeader title={configSections.venueTypeHierarchy.title} valid={isVenueTypesValid} />
                        <AccordionDetails>
                            <EditVenueTypesComponent
                                onChange={(types) => venueTypesChanged(types)}
                                allDataFields={schedule?.config.dataFields}
                                onDataFieldsChanged={dataFieldsChanged}
                                venueLocationTypes={schedule?.config.venueLocationTypes}
                                isPreviouslySaved={isScheduleConfigPreviouslySaved.current}
                            />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion
                        disabled={apiSchedule.isLoading}
                        expanded={expandedPanel === configSections.scheduleVersionHierarchy.key}
                        onChange={(_, expanded) => (expanded ? setExpandedPanel(configSections.scheduleVersionHierarchy.key) : setExpandedPanel(undefined))}
                        TransitionProps={{ unmountOnExit: true }}
                    >
                        <ConfigSectionHeader title={configSections.scheduleVersionHierarchy.title} valid={isScheduleVersionValid} />
                        <AccordionDetails>
                            <EditScheduleVersionTypes
                                scheduleVersionTypes={schedule?.config.scheduleVersionTypes}
                                dataFields={schedule?.config.dataFields}
                                onValueChange={scheduleVersionTypeChanged}
                            />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion
                        disabled={apiSchedule.isLoading}
                        TransitionProps={{ unmountOnExit: true }}
                        expanded={expandedPanel === configSections.eventBlockHierarchy.key}
                        onChange={(_, expanded) => (expanded ? setExpandedPanel(configSections.eventBlockHierarchy.key) : setExpandedPanel(undefined))}
                    >
                        <ConfigSectionHeader title={configSections.eventBlockHierarchy.title} valid={isEventBlockTypesValid} />
                        <AccordionDetails>
                            <EditEventBlockTypesComponent
                                eventBlockTypes={schedule?.config.eventBlockTypes}
                                venueTypes={schedule?.config.venueLocationTypes}
                                dataFields={schedule?.config.dataFields}
                                onValueChange={eventBlockTypeChanged}
                                onDataFieldsChange={dataFieldsChanged}
                            />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion
                        disabled={apiSchedule.isLoading}
                        expanded={expandedPanel === configSections.venueRunSheets.key}
                        onChange={(_, expanded) => (expanded ? setExpandedPanel(configSections.venueRunSheets.key) : setExpandedPanel(undefined))}
                        TransitionProps={{ unmountOnExit: true }}
                    >
                        <ConfigSectionHeader title={configSections.venueRunSheets.title} valid={isRunSheetValid} />
                        <AccordionDetails>
                            <EditDcas
                                dcasConfig={schedule?.config.dcasConfig}
                                dataFields={schedule?.config.dataFields}
                                eventBlockTypes={schedule?.config.eventBlockTypes}
                                venueTypes={schedule?.config.venueLocationTypes}
                                onDataFieldsChange={dataFieldsChanged}
                                onEventBlockChange={eventBlockTypeChanged}
                                onDcasChange={dcasChange}
                            />
                        </AccordionDetails>
                    </Accordion>
                </LocalizationProvider>
            </StyledProjectScheduleConfigContainer>
        );
    });

const StyledValidationErrors = styled.section<{ show: boolean }>`
    display: ${({ show }) => (show ? "block" : "none")};
    border-radius: ${borderRadius.standard};
    border: 1px solid ${({ theme }: { theme: Theme }) => theme.primaryColors.focus};
    background-color: ${({ theme }: { theme: Theme }) => theme.secondaryColors.light20};
    margin-bottom: ${inputMarginBottom};
    overflow: hidden;
    height: 0;

    .padding {
        margin: ${formPadding};
    }

    transition: height 0.3s ease-in-out;

    h5 {
        margin-bottom: ${labelMargin};
    }

    li * {
        display: inline;
    }

    svg {
        transform: scale(0.5) translateY(2px);
        margin-right: ${inlineTextIconMargin};
    }
`;

const StyledProjectScheduleConfigContainer = styled.div`
    ${muiInputFormsCSS}

    max-width: 1050px;
    width: 100%;
    margin-left: auto;
    margin-right: auto;

    // Fixes issue of Accordion being off center from padding set in muiInputFormsCSS
    ${media.extraSmall} {
        padding: 0px;
    }

    .MuiAccordionSummary-content {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .valid-icon {
            color: ${({ theme }: { theme: Theme }) => theme.secondaryColors.accept};
        }

        .invalid-icon {
            color: ${({ theme }: { theme: Theme }) => theme.secondaryColors.deny};
        }
    }
`;
